import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faUpload, faEye, faDownload, faTrash, faSpinner, faFile, faFilePdf, faFileImage, faFileExcel, faFileWord, faTimes } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import attachmentService from '../../services/attachmentService';
import ConfirmDialog from './ConfirmDialog';

const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'];
const MAX_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB

const getFileIcon = (fileName) => {
    const ext = (fileName || '').split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return faFilePdf;
    if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return faFileImage;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return faFileExcel;
    if (['doc', 'docx'].includes(ext)) return faFileWord;
    return faFile;
};

const formatBytes = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const AttachmentsPanel = ({ entityType, entityId, subOrderId, adviceId, contextLabel = '' }) => {
    const { t } = useTranslation();
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [confirmAttachment, setConfirmAttachment] = useState(null);
    const [error, setError] = useState(null);
    const [pendingFile, setPendingFile] = useState(null);   // file chosen, awaiting remark + upload
    const [pendingRemark, setPendingRemark] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const fileInputRef = useRef(null);

    const lastKeyRef = useRef(null);
    const dataLoadedRef = useRef(false);

    useEffect(() => {
        const key = subOrderId ? `sub-${subOrderId}`
            : adviceId ? `adv-${adviceId}`
            : (entityType && entityId) ? `${entityType}-${entityId}`
            : null;

        if (!key) return;
        if (key === lastKeyRef.current && dataLoadedRef.current) return;
        if (key !== lastKeyRef.current) { lastKeyRef.current = key; dataLoadedRef.current = false; }

        let cancelled = false;
        const run = async () => {
            setLoading(true);
            setError(null);
            try {
                let res;
                if (subOrderId) res = await attachmentService.listForSubOrder(subOrderId);
                else if (adviceId) res = await attachmentService.listForAdvice(adviceId);
                else res = await attachmentService.list(entityType, entityId);
                if (cancelled) return;
                dataLoadedRef.current = true;
                if (res.success) setAttachments(Array.isArray(res.data) ? res.data : []);
                else setError(res.error || 'Failed to load attachments');
            } catch (err) {
                if (!cancelled) setError(err?.message || 'Failed to load attachments');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [entityType, entityId, subOrderId, adviceId]);

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        const ext = file.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) { setError(`File type .${ext} not allowed.`); return; }
        if (file.size > MAX_SIZE_BYTES) { setError('File too large. Max 20 MB.'); return; }
        setError(null);
        setPendingFile(file);
        setPendingRemark('');
    };

    const handleUploadConfirm = async () => {
        if (!pendingFile) return;
        setUploading(true);
        setError(null);
        try {
            const remark = pendingRemark.trim();
            let res;
            if (subOrderId) res = await attachmentService.uploadForSubOrder(subOrderId, pendingFile, remark);
            else if (adviceId) res = await attachmentService.uploadForAdvice(adviceId, pendingFile, remark);
            else res = await attachmentService.upload(entityType, entityId, pendingFile, remark);
            if (res.success) {
                setAttachments(prev => [res.data, ...prev]);
                setPendingFile(null);
                setPendingRemark('');
                setIsOpen(true);
            } else {
                setError(res.error || 'Upload failed');
            }
        } catch (err) {
            setError(err?.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handlePreview = async (attachment) => {
        const res = await attachmentService.download(attachment.id, { inline: true });
        if (res.success && res.data?.url) window.open(res.data.url, '_blank', 'noopener,noreferrer');
        else setError(res.error || 'Preview failed');
    };

    const handleDownload = async (attachment) => {
        const res = await attachmentService.download(attachment.id, { inline: false });
        if (res.success && res.data?.url) window.open(res.data.url, '_blank', 'noopener,noreferrer');
        else setError(res.error || 'Download failed');
    };

    const handleDelete = (attachment) => setConfirmAttachment(attachment);

    const handleDeleteConfirm = async () => {
        const attachment = confirmAttachment;
        setConfirmAttachment(null);
        setDeletingId(attachment.id);
        try {
            const res = await attachmentService.delete(attachment.id);
            if (res.success) setAttachments(prev => prev.filter(a => a.id !== attachment.id));
            else setError(res.error || 'Delete failed');
        } catch (err) {
            setError(err?.message || 'Delete failed');
        } finally {
            setDeletingId(null);
        }
    };

    const hasAttachments = attachments.length > 0;

    return (
        <div className="mt-4">
            {/* Header row */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faPaperclip} className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }} />
                    <span className="text-sm font-bold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                        Attachments{contextLabel ? ` — ${contextLabel}` : ''}
                    </span>
                    {hasAttachments && (
                        <span className="text-xs font-black px-2 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: attachments.length > 0 ? '#dc2626' : UI_COLORS.BRAND.PRIMARY }}>
                            {attachments.length}
                        </span>
                    )}
                    {hasAttachments && (
                        <button
                            type="button"
                            onClick={() => setIsOpen(!isOpen)}
                            className="ml-2 px-2 py-1 rounded-lg text-xs font-semibold hover:bg-gray-100 border transition-colors flex items-center gap-1"
                            style={{ color: UI_COLORS.TEXT.PRIMARY, borderColor: UI_COLORS.BORDERS.LIGHT }}
                        >
                            <FontAwesomeIcon icon={isOpen ? faTimes : faEye} className="text-xs" />
                            {isOpen ? 'Close' : 'View'}
                        </button>
                    )}
                </div>
                <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || !!pendingFile}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all hover:scale-105 disabled:opacity-50"
                    style={{ background: '#111', color: '#fff' }}
                >
                    <FontAwesomeIcon icon={faUpload} />
                    Attach File
                </button>
                <input ref={fileInputRef} type="file" className="hidden"
                    accept={ALLOWED_EXTENSIONS.map(e => `.${e}`).join(',')}
                    onChange={handleFileSelect}
                />
            </div>

            {/* Error */}
            {error && (
                <p className="text-xs mb-2 px-3 py-2 rounded-lg" style={{ color: UI_COLORS.STATUS.ERROR, background: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08) }}>
                    {error}
                </p>
            )}

            {/* Pending upload panel */}
            {pendingFile && (
                <div className="mb-3 rounded-xl border-2 p-3 space-y-2" style={{ borderColor: UI_COLORS.BRAND.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.04) }}>
                    <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={getFileIcon(pendingFile.name)} className="text-sm flex-shrink-0" style={{ color: UI_COLORS.TEXT.SECONDARY }} />
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{pendingFile.name}</p>
                            <p className="text-[10px]" style={{ color: UI_COLORS.TEXT.MUTED }}>{formatBytes(pendingFile.size)}</p>
                        </div>
                        <button onClick={() => { setPendingFile(null); setPendingRemark(''); }} className="p-1 rounded" style={{ color: UI_COLORS.TEXT.MUTED }}>
                            <FontAwesomeIcon icon={faTimes} className="text-xs" />
                        </button>
                    </div>
                    <input
                        type="text"
                        value={pendingRemark}
                        onChange={e => setPendingRemark(e.target.value)}
                        placeholder={t('commonForm.shortRemarkOptional')}
                        className="w-full px-3 py-1.5 text-xs rounded-lg border"
                        style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                        onKeyDown={e => { if (e.key === 'Enter') handleUploadConfirm(); }}
                        autoFocus
                    />
                    <div className="flex justify-end gap-2">
                        <button onClick={() => { setPendingFile(null); setPendingRemark(''); }}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                            style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1) }}>
                            Cancel
                        </button>
                        <button onClick={handleUploadConfirm} disabled={uploading}
                            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold text-white disabled:opacity-60"
                            style={{ background: '#111' }}>
                            {uploading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : <FontAwesomeIcon icon={faUpload} />}
                            {uploading ? 'Uploading…' : 'Upload'}
                        </button>
                    </div>
                </div>
            )}

            {/* Attachments list */}
            {isOpen && (
                <div className="rounded-xl border overflow-hidden mt-3" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    {loading ? (
                        <div className="flex items-center justify-center py-8 gap-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                            <span className="text-xs">Loading…</span>
                        </div>
                    ) : !hasAttachments ? (
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="w-full py-8 flex flex-col items-center gap-2 transition-colors hover:bg-gray-50/80"
                            style={{ color: UI_COLORS.TEXT.MUTED }}
                        >
                            <FontAwesomeIcon icon={faPaperclip} className="text-2xl opacity-30" />
                            <span className="text-xs font-medium">No attachments — click to add</span>
                        </button>
                    ) : (
                        <>
                            <div className="divide-y" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                {attachments.map(att => {
                                    const ext = (att.file_name || '').split('.').pop().toLowerCase();
                                    const extColors = {
                                        pdf: { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
                                        png: { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
                                        jpg: { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
                                        jpeg: { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
                                        webp: { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
                                        xls: { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' },
                                        xlsx: { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' },
                                        csv: { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' },
                                        doc: { bg: '#eef2ff', text: '#4338ca', border: '#c7d2fe' },
                                        docx: { bg: '#eef2ff', text: '#4338ca', border: '#c7d2fe' },
                                    };
                                    const badge = extColors[ext] || { bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' };
                                    return (
                                        <div key={att.id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50/60 transition-colors">
                                            {/* File type badge */}
                                            <div className="flex-shrink-0 w-10 h-10 rounded-lg flex flex-col items-center justify-center border"
                                                style={{ backgroundColor: badge.bg, borderColor: badge.border }}>
                                                <FontAwesomeIcon icon={getFileIcon(att.file_name)} className="text-sm" style={{ color: badge.text }} />
                                                <span className="text-[8px] font-black uppercase mt-0.5 leading-none" style={{ color: badge.text }}>{ext}</span>
                                            </div>
                                            {/* Info */}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold leading-tight" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                    {att.file_name}
                                                </p>
                                                {(att.remark || att.description) && (
                                                    <span className="inline-block mt-1 px-2 py-0.5 rounded text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                                                        {att.remark || att.description}
                                                    </span>
                                                )}
                                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                                    {att.size_bytes > 0 && (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 font-medium" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                                            {formatBytes(att.size_bytes)}
                                                        </span>
                                                    )}
                                                    {att.uploader_name && (
                                                        <span className="text-[10px] font-medium" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                                            by {att.uploader_name}
                                                        </span>
                                                    )}
                                                    {att.uploaded_at && (
                                                        <span className="text-[10px]" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                                            {formatDate(att.uploaded_at)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Actions */}
                                            <div className="flex items-center gap-1 flex-shrink-0 pt-0.5">
                                                <button onClick={() => handlePreview(att)}
                                                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors hover:bg-blue-50"
                                                    title={t('common.actions.preview')} style={{ color: UI_COLORS.STATUS.INFO }}>
                                                    <FontAwesomeIcon icon={faEye} className="text-xs" />
                                                    <span>View</span>
                                                </button>
                                                <button onClick={() => handleDownload(att)}
                                                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors hover:bg-green-50"
                                                    title={t('common.actions.download')} style={{ color: UI_COLORS.STATUS.SUCCESS }}>
                                                    <FontAwesomeIcon icon={faDownload} className="text-xs" />
                                                    <span>Save</span>
                                                </button>
                                                <button onClick={() => handleDelete(att)} disabled={deletingId === att.id}
                                                    className="p-1.5 rounded-lg transition-colors hover:bg-red-50"
                                                    title={t('common.actions.delete')} style={{ color: UI_COLORS.STATUS.ERROR }}>
                                                    <FontAwesomeIcon icon={deletingId === att.id ? faSpinner : faTrash} className={`text-xs ${deletingId === att.id ? 'animate-spin' : ''}`} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex justify-end p-2 bg-gray-50 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="px-3 py-1 rounded-lg text-xs font-semibold hover:bg-gray-200 border transition-colors flex items-center gap-1"
                                    style={{ color: UI_COLORS.TEXT.PRIMARY, borderColor: UI_COLORS.BORDERS.LIGHT }}
                                >
                                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                                    Close List
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            <ConfirmDialog
                isOpen={!!confirmAttachment}
                title={t('common.actions.deleteAttachment')}
                message={`Delete "${confirmAttachment?.file_name}"? This cannot be undone.`}
                confirmLabel="Delete"
                confirmVariant="danger"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setConfirmAttachment(null)}
            />

            {(subOrderId || adviceId) && (
                <p className="text-[10px] mt-1.5 italic" style={{ color: UI_COLORS.TEXT.MUTED }}>
                    Attachments shared across all sub-orders / advices for this {subOrderId ? 'order' : 'purchase order'}.
                </p>
            )}
        </div>
    );
};

export default AttachmentsPanel;
